import { useEffect, useState } from "react";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle, Pill } from "./shared";
import { money, TIERS } from "../portalData";
import {
  partnerGetDashboard,
  partnerGetSessions,
  getStoredPartnerClient,
} from "../../../services/partnerCalls";
import { errorHandler } from "../../../utils";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import { Spinner } from "../../../components/UI/Spinner/Spinner";
import { displayRole } from "../partnerRoleCatalog";

const getStoredClientName = () => getStoredPartnerClient()?.name || "there";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [currency, setCurrency] = useState("NGN");

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await partnerGetDashboard();
      if (res?.data?.data) {
        setStats(res.data.data);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    setSessionLoading(true);
    try {
      const res = await partnerGetSessions({ limit: 5 });
      if (res?.data?.data?.sessions) {
        setSessions(res.data.data.sessions);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchSessions();
  }, []);

  const greeting = `Welcome back, ${getStoredClientName()}`;

  if (loading || sessionLoading) {
    return (
      <div>
        <SectionTitle title="Dashboard" sub={greeting} />
        <Panel>
          <Spinner text="Loading your dashboard..." />
        </Panel>
      </div>
    );
  }

  const balance = stats?.balance ?? 0;
  const allocation = stats?.allocation ?? 0;
  const pct = allocation > 0 ? Math.round((balance / allocation) * 100) : 0;
  const myTier = TIERS.find((t) => t.name.toLowerCase() === (stats?.tier_name || "").toLowerCase());

  return (
    <div>
      <SectionTitle title="Dashboard" sub={greeting} />

      <div className={s.grid3}>
        <Panel>
          <div className={s.cardLabel}>Credit balance</div>
          <div className={s.pg1}
          >
            <span className={s.cardBig}>{balance?.toLocaleString()}</span>
            <span className={s.pg2}>
              / {allocation?.toLocaleString()} credits
            </span>
          </div>
          <div className={s.track}>
            <div
              className={`${s.fill} ${pct < 20 ? s.fillLow : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className={s.pg3}>
            {pct}% remaining{pct < 20 ? " · balance is running low" : ""}
          </div>
        </Panel>

        <Panel>
          <div className={s.cardLabel}>Usage this month</div>
          <div className={s.cardBig}>
            {(stats?.usage_this_month ?? 0)?.toLocaleString()}
          </div>
        </Panel>

        <Panel>
          <div className={s.cardLabel}>Webhook success</div>
          <div className={s.cardBig}>{stats?.webhook_success_rate ?? 0}%</div>
          <div className={s.cardHint}>last 30 days</div>
        </Panel>
      </div>

      <div className={s.grid2}>
        <Panel>
          <div className={s.blockTitle}>Recent test sessions</div>
          {sessions?.length ? (
            sessions?.map((session) => (
              <div key={session?.session_id} className={s.rowItem}>
                <div>
                  <div className={s.rowName}>{session?.candidate_email}</div>
                  <div className={s.rowMeta}>
                    {displayRole(
                      session?.test_role ||
                        (typeof session?.test_id === "object" ? session.test_id?.role : null) ||
                        (typeof session?.rubric_test_id === "object" ? session.rubric_test_id?.role : null) ||
                        "",
                    ) || "—"}
                  </div>
                </div>
                <Pill status={session?.status} />
              </div>
            ))
          ) : (
            <div className={s.rowMeta}>No sessions yet.</div>
          )}
        </Panel>

        <Panel sunken>
          <div className={s.blockTitle}>Quick start</div>
          <div className={s.code}>
            <div className={s.codeDim}># create a test session</div>
            <div>
              <span className={s.codeTeal}>POST</span> /v1/sessions
            </div>
            <div className={s.codeDim}>{"{"}</div>
            <div>&nbsp;&nbsp;"candidate": "a.bello@itm.com",</div>
            <div>&nbsp;&nbsp;"role": "Frontend Developer"</div>
            <div className={s.codeDim}>{"}"}</div>
            <div className={`${s.codeDim} ${s.pg4}`}>
              # → returns test_url + session_id
            </div>
          </div>
          <div className={s.note}>
            {myTier ? (
              <>
                A 3-credit Business Analyst test costs {money(myTier.per * 3, currency)} at
                your {myTier.name} tier.
              </>
            ) : (
              "A 3-credit Business Analyst test costs 3 credits."
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
